import { observer } from 'mobx-react';
import React, { useEffect, useState } from 'react';
import Paper from '@mui/material/Paper';
import {
  Box,
  Button,
  Checkbox,
  Chip,
  FormControl,
  Grid,
  InputLabel,
  ListItemText,
  MenuItem,
  OutlinedInput,
  Select,
  Stack,
  TextField,
  Typography,
  createTheme,
  useMediaQuery,
} from '@mui/material';
import './style.css';
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFnsV3';
import { enIN } from 'date-fns/locale';
import LeadStore from '../../store/LeadStore';
import dayjs from 'dayjs';
import { isEmpty } from '../../utils/helper';

const ITEM_HEIGHT = 48;
const ITEM_PADDING_TOP = 8;
const MenuProps = {
  PaperProps: {
    style: {
      maxHeight: ITEM_HEIGHT * 4.5 + ITEM_PADDING_TOP,
      width: 250,
    },
  },
};

export const EditLeadKycDetails = observer(({ leadId, kycDetails, handleEditLeadKycDetails }) => {
  const theme = createTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down('sm'));

  const [formData, setFormData] = useState({
    id: kycDetails?.id ?? null,
    leadId,
    clientName: kycDetails?.clientName,
    clientOccupation: kycDetails?.clientOccupation,
    clientResidingAddress: kycDetails?.clientResidingAddress,
    clientDob: kycDetails?.clientDob,
    clientDependentDob: kycDetails?.clientDependentDob,
    clientMaritalStatus: kycDetails?.clientMaritalStatus,
    clientAnniversary: kycDetails?.clientAnniversary,
    tentativeProjectValue: kycDetails?.tentativeProjectValue,
    signedUpPackage: kycDetails?.signedUpPackage,
    projectManager: kycDetails?.projectManager,
    architectConcerned: kycDetails?.architectConcerned,
    modeOfPayment: kycDetails?.modeOfPayment,
    paymentReceivedBy: kycDetails?.paymentReceivedBy,
    documentsReceived: kycDetails?.documentsReceived?.split(','),
    documentsReceivedOther: kycDetails?.documentsReceivedOther,
    contactNumber: kycDetails?.contactNumber,
    alternativeNumber: kycDetails?.alternativeNumber,
    gpssiteLocation: kycDetails?.gpssiteLocation,
    siteCondition: kycDetails?.siteCondition?.split(','),
    siteConditionOther: kycDetails?.siteConditionOther,
    nearByOngoingProjects: kycDetails?.nearByOngoingProjects,
    liasioningOrGovernmentWork: kycDetails?.liasioningOrGovernmentWork?.split(','),
    liasioningOrGovernmentWorkOther: kycDetails?.liasioningOrGovernmentWorkOther,
    preliminaryMeeting: kycDetails?.preliminaryMeeting,
    processCompleted: kycDetails?.processCompleted?.split(','),
    processCompletedOther: kycDetails?.processCompletedOther,
    additionalServicesPromised: kycDetails?.additionalServicesPromised,
    dateOfSignUp: kycDetails?.dateOfSignUp,
    tentativeAgreementDate: kycDetails?.tentativeAgreementDate,
    createdBy: kycDetails?.CreatedBy,
    createdDate: kycDetails?.createdDate,
    updatedBy: kycDetails?.updatedBy,
    updatedDate: kycDetails?.updatedDate,
  });

  const [errors, setErrors] = useState({
    clientName: false,
    signedUpPackage: false,
    modeOfPayment: false,
    paymentReceivedBy: false,
    documentsReceived: false,
    documentsReceivedOther: false,
    contactNumber: false,
    alternativeNumber: false,
    gpssiteLocation: false,
    siteCondition: false,
    siteConditionOther: false,
    nearByOngoingProjects: false,
    processCompleted: false,
    processCompletedOther: false,
    liasioningOrGovernmentWork: false,
    liasioningOrGovernmentWorkOther: false,
  });

  const [errorMessages, setErrorMessages] = useState({
    clientName: '',
    signedUpPackage: '',
    modeOfPayment: '',
    paymentReceivedBy: '',
    documentsReceived: '',
    documentsReceivedOther: '',
    contactNumber: '',
    alternativeNumber: '',
    gpssiteLocation: '',
    siteCondition: '',
    siteConditionOther: '',
    nearByOngoingProjects: '',
    processCompleted: '',
    processCompletedOther: '',
    liasioningOrGovernmentWork: '',
    liasioningOrGovernmentWorkOther: '',
  });

  const siteConditionsList = [
    { id: 1, name: 'Slope Land' },
    { id: 2, name: '< 5 Km from Ring Road' },
    { id: 3, name: '< 10 Km from Ring Road' },
    { id: 4, name: 'Water Logging' },
    { id: 5, name: 'Loose soil' },
    { id: 6, name: 'Logistics Challenges' },
    { id: 999, name: 'Other' },
  ];

  const documentsReceivedList = [
    { id: 1, name: 'Check Bandi ( Schedule of property )' },
    { id: 2, name: 'Aadhar Card' },
    { id: 3, name: 'PAN Card' },
    { id: 999, name: 'Other' },
  ];

  const liasioningOrGovernmentWorkList = [
    { id: 1, name: 'Plan approval' },
    { id: 2, name: 'Temporary water Connection' },
    { id: 3, name: 'Temporary electrical Connection' },
    { id: 4, name: 'Site stone Marking' },
    { id: 5, name: 'Electrical pole shifting' },
    { id: 6, name: 'Bore well' },
    { id: 7, name: 'Bank Loan' },
    { id: 8, name: 'Demolition' },
    { id: 999, name: 'Other' },
  ];

  const processCompletedList = [
    { id: 1, name: 'Sales Meeting' },
    { id: 2, name: 'Office Tour' },
    { id: 3, name: 'Site Tour' },
    { id: 4, name: 'Client Site Visit' },
    { id: 5, name: 'Project Orientation' },
    { id: 6, name: 'Introduction of PM & Architect' },
    { id: 7, name: 'Documents shared to inter departments' },
    { id: 8, name: 'Explanation of package details for PM & Architect' },
    { id: 999, name: 'Other' },
  ];

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });

    // Perform validation
    if (name === 'contactNumber') {
      setErrors({ ...errors, [name]: isContactNumberError(value, name) });
    } else if (name === 'alternativeNumber') {
      setErrors({ ...errors, [name]: isNumberError(value, name) });
    } else {
      setErrors({ ...errors, [name]: isEmpty(value) });
    }
  };

  const handleUpdateLeadKycSubmit = (e) => {
    e.preventDefault();

    const updatedErrors = {
      clientName: isEmpty(formData.clientName),
      signedUpPackage: isEmpty(formData.signedUpPackage),
      modeOfPayment: isEmpty(formData.modeOfPayment),
      paymentReceivedBy: isEmpty(formData.paymentReceivedBy),
      documentsReceived: isEmpty(formData.documentsReceived),
      documentsReceivedOther: formData.documentsReceived?.indexOf('Other') > -1 && isEmpty(formData.documentsReceived),
      contactNumber: isContactNumberError(formData.contactNumber),
      alternativeNumber: isNumberError(formData.alternativeNumber, 'alternativeNumber'),
      gpssiteLocation: isEmpty(formData.gpssiteLocation),
      siteCondition: isEmpty(formData.siteCondition),
      siteConditionOther: formData.siteCondition?.indexOf('Other') > -1 && isEmpty(formData.siteConditionOther),
      nearByOngoingProjects: isEmpty(formData.nearByOngoingProjects),
      processCompleted: isEmpty(formData.processCompleted),
      processCompleted: formData.processCompleted?.indexOf('Other') > -1 && isEmpty(formData.processCompletedOther),
      liasioningOrGovernmentWork: isEmpty(formData.liasioningOrGovernmentWork),
      liasioningOrGovernmentWorkOther:
        formData.liasioningOrGovernmentWork?.indexOf('Other') > -1 && isEmpty(formData.liasioningOrGovernmentWorkOther),
    };
    setErrors(updatedErrors);

    if (!Object.values(updatedErrors).some((error) => error === true)) {
      const payload = { ...formData };
      payload.siteCondition = payload.siteCondition?.join(',');
      payload.documentsReceived = payload.documentsReceived?.join(',');
      payload.liasioningOrGovernmentWork = payload.liasioningOrGovernmentWork?.join(',');
      payload.processCompleted = payload.processCompleted?.join(',');
      isEmpty(kycDetails) ? LeadStore.createCustomerKyc(payload) : LeadStore.updateCustomerKyc(payload);
    }
  };

  useEffect(() => {
    LeadStore.updateCustomerKycSuccess && handleEditLeadKycDetails();
  }, [LeadStore.updateCustomerKycSuccess]);

  const isContactNumberError = (value, name) => {
    if (isEmpty(value)) {
      return true;
    } else if (!/^\d{0,10}$/.test(value)) {
      setErrorMessages({
        ...errors,
        [name]: 'Please enter valid 10 digit number',
      });
      return true;
    } else {
      return false;
    }
  };

  const isNumberError = (value, name) => {
    if (isEmpty(value)) {
      return false;
    } else if (!/^\d{0,10}$/.test(value)) {
      setErrorMessages({
        ...errors,
        [name]: 'Please enter valid 10 digit number',
      });
      return true;
    } else {
      return false;
    }
  };

  return (
    <Paper sx={{ px: '30px', py: '20px', pb: '40px' }}>
      <Box
        id="lead-kyc-details-form"
        component="form"
        noValidate
        autoComplete="off"
        onSubmit={(event) => {
          event.preventDefault();
          handleUpdateLeadKycSubmit(event);
        }}
      >
        <Stack spacing={4}>
          <Stack spacing={3}>
            <Grid rowGap={2} container>
              <Grid item xs={12} sm={6} md={6} lg={6} sx={{ pr: isSmallScreen ? '0px' : '10px' }}>
                <Stack direction={'column'} spacing={3}>
                  <TextField
                    label="Client Name"
                    required
                    id="client-name"
                    size="small"
                    variant="standard"
                    name="clientName"
                    value={formData.clientName ?? ''}
                    onChange={(e) => handleChange(e)}
                    error={Boolean(errors.clientName)}
                    sx={{ fontSize: '12px' }}
                    InputLabelProps={{
                      shrink: true,
                    }}
                  />
                  <TextField
                    id="clientOccupation"
                    name="clientOccupation"
                    label="Client Occupation"
                    size="small"
                    variant="standard"
                    value={formData.clientOccupation}
                    onChange={handleChange}
                    error={Boolean(errors.clientOccupation)}
                    fullWidth
                    sx={{ fontSize: '12px' }}
                    InputLabelProps={{
                      shrink: true,
                    }}
                  />
                  <TextField
                    label="Client residing address"
                    id="clientResidingAddress"
                    size="small"
                    variant="standard"
                    name="clientResidingAddress"
                    value={formData.clientResidingAddress ?? ''}
                    onChange={(e) => handleChange(e)}
                    error={Boolean(errors.clientResidingAddress)}
                    sx={{ fontSize: '12px' }}
                    InputLabelProps={{
                      shrink: true,
                    }}
                  />
                  <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={enIN}>
                    <DatePicker
                      label="Client Dob"
                      value={formData.clientDob ? new Date(formData.clientDob) : null}
                      onChange={(newValue) => {
                        handleChange({
                          target: { name: 'clientDob', value: newValue },
                        });
                      }}
                      slotProps={{
                        textField: {
                          size: 'small',
                          variant: 'standard',
                          name: 'clientDob',
                          fontSize: '12px',
                          InputLabelProps: { shrink: true },
                        },
                      }}
                    />
                  </LocalizationProvider>
                  <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={enIN}>
                    <DatePicker
                      label="Client Dependent Dob"
                      value={formData.clientDependentDob ? new Date(formData.clientDependentDob) : null}
                      onChange={(newValue) => {
                        handleChange({
                          target: {
                            name: 'clientDependentDob',
                            value: newValue,
                          },
                        });
                      }}
                      slotProps={{
                        textField: {
                          size: 'small',
                          variant: 'standard',
                          name: 'clientDependentDob',
                          fontSize: '12px',
                          InputLabelProps: { shrink: true },
                        },
                      }}
                    />
                  </LocalizationProvider>
                  <TextField
                    label="Client Marital Status"
                    id="clientMaritalStatus"
                    size="small"
                    variant="standard"
                    name="clientMaritalStatus"
                    value={formData.clientMaritalStatus ?? ''}
                    onChange={(e) => handleChange(e)}
                    error={Boolean(errors.clientMaritalStatus)}
                    sx={{ fontSize: '12px' }}
                    InputLabelProps={{
                      shrink: true,
                    }}
                  />
                  <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={enIN}>
                    <DatePicker
                      label="Client Anniversary"
                      value={formData.clientAnniversary ? new Date(formData.clientAnniversary) : null}
                      onChange={(newValue) => {
                        handleChange({
                          target: {
                            name: 'clientAnniversary',
                            value: newValue,
                          },
                        });
                      }}
                      slotProps={{
                        textField: {
                          size: 'small',
                          variant: 'standard',
                          name: 'clientAnniversary',
                          fontSize: '12px',
                          InputLabelProps: { shrink: true },
                        },
                      }}
                    />
                  </LocalizationProvider>
                  <TextField
                    label="Tentative Project Value"
                    id="tentativeProjectValue"
                    size="small"
                    type="number"
                    variant="standard"
                    sx={{ fontSize: '12px' }}
                    name="tentativeProjectValue"
                    maxLength="300"
                    value={formData.tentativeProjectValue ?? ''}
                    onChange={handleChange}
                    error={Boolean(errors.tentativeProjectValue)}
                    InputLabelProps={{
                      shrink: true,
                    }}
                  />
                  <TextField
                    label="Signed up package"
                    required
                    id="signedUpPackage"
                    size="small"
                    variant="standard"
                    name="signedUpPackage"
                    value={formData.signedUpPackage ?? ''}
                    onChange={(e) => handleChange(e)}
                    error={Boolean(errors.signedUpPackage)}
                    sx={{ fontSize: '12px' }}
                    InputLabelProps={{
                      shrink: true,
                    }}
                  />
                  <TextField
                    label="Project Manager"
                    id="projectManager"
                    size="small"
                    variant="standard"
                    name="projectManager"
                    value={formData.projectManager ?? ''}
                    onChange={(e) => handleChange(e)}
                    error={Boolean(errors.projectManager)}
                    sx={{ fontSize: '12px' }}
                    InputLabelProps={{
                      shrink: true,
                    }}
                  />
                  <TextField
                    label="Architect Concerned"
                    id="architectConcerned"
                    size="small"
                    variant="standard"
                    name="architectConcerned"
                    value={formData.architectConcerned ?? ''}
                    onChange={(e) => handleChange(e)}
                    error={Boolean(errors.architectConcerned)}
                    sx={{ fontSize: '12px' }}
                    InputLabelProps={{
                      shrink: true,
                    }}
                  />
                  <TextField
                    label="Mode of Payment"
                    required
                    id="modeOfPayment"
                    size="small"
                    variant="standard"
                    name="modeOfPayment"
                    value={formData.modeOfPayment ?? ''}
                    onChange={(e) => handleChange(e)}
                    error={Boolean(errors.modeOfPayment)}
                    sx={{ fontSize: '12px' }}
                    InputLabelProps={{
                      shrink: true,
                    }}
                  />
                  <TextField
                    label="Payment Received By"
                    required
                    id="paymentReceivedBy"
                    size="small"
                    variant="standard"
                    name="paymentReceivedBy"
                    value={formData.paymentReceivedBy ?? ''}
                    onChange={(e) => handleChange(e)}
                    error={Boolean(errors.paymentReceivedBy)}
                    sx={{ fontSize: '12px' }}
                    InputLabelProps={{
                      shrink: true,
                    }}
                  />
                  <FormControl variant="standard" required error={Boolean(errors.documentsReceived)}>
                    <InputLabel id="documentsReceivedLabel" required shrink={true}>
                      Documents Received from clients
                    </InputLabel>
                    <Select
                      labelId="documentsReceivedLabel"
                      id="documentsReceived"
                      name="documentsReceived"
                      required
                      value={formData.documentsReceived ?? []}
                      multiple
                      onChange={(e) => {
                        const {
                          target: { value },
                        } = e;
                        setFormData({
                          ...formData,
                          ['documentsReceived']: typeof value === 'string' ? value.split(',') : value,
                          ['documentsReceivedOther']: isEmpty(value.find((item) => item === 'Other'))
                            ? null
                            : formData.documentsReceivedOther,
                        });
                        setErrors({
                          ...errors,
                          ['documentsReceived']: isEmpty(value),
                        });
                      }}
                      label="Documents Received from clients"
                      renderValue={(selected) => (
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                          {selected.map((value) => (
                            <Chip key={value} label={value} />
                          ))}
                        </Box>
                      )}
                      MenuProps={MenuProps}
                    >
                      {documentsReceivedList.map((site) => (
                        <MenuItem key={site.id} value={site.name}>
                          <Checkbox checked={formData.documentsReceived?.indexOf(site.name) > -1} />
                          <ListItemText primary={site.name} />
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                  {formData.documentsReceived?.indexOf('Other') > -1 && (
                    <TextField
                      label="Documents Received Other"
                      id="documentsReceivedOther"
                      size="small"
                      variant="standard"
                      required={formData.documentsReceived?.indexOf('Other') > -1}
                      name="documentsReceivedOther"
                      multiline
                      rows={1}
                      value={formData.documentsReceivedOther ?? ''}
                      onChange={(e) => handleChange(e)}
                      error={Boolean(errors.documentsReceivedOther)}
                      sx={{ fontSize: '12px' }}
                      InputLabelProps={{
                        shrink: true,
                      }}
                    />
                  )}
                </Stack>
              </Grid>
              <Grid item xs={12} sm={6} md={6} lg={6} sx={{ pl: isSmallScreen ? '0px' : '10px' }}>
                <Stack direction={'column'} spacing={3}>
                  <TextField
                    label="Contact Number (Primary)"
                    required
                    id="contactNumber"
                    size="small"
                    variant="standard"
                    name="contactNumber"
                    sx={{ fontSize: '12px' }}
                    value={formData.contactNumber ?? ''}
                    onChange={handleChange}
                    error={Boolean(errors.contactNumber)}
                    helperText={Boolean(errors.contactNumber) && errorMessages.contactNumber}
                    InputLabelProps={{
                      shrink: true,
                    }}
                  />
                  <TextField
                    label="Contact Number (Secondary)"
                    id="alternativeNumber"
                    size="small"
                    variant="standard"
                    name="alternativeNumber"
                    sx={{ fontSize: '12px' }}
                    value={formData.alternativeNumber ?? ''}
                    onChange={handleChange}
                    error={Boolean(errors.alternativeNumber)}
                    helperText={Boolean(errors.alternativeNumber) && errorMessages.alternativeNumber}
                    InputLabelProps={{
                      shrink: true,
                    }}
                  />
                  <TextField
                    label="GPS Site Location"
                    required
                    id="gpssiteLocation"
                    size="small"
                    variant="standard"
                    name="gpssiteLocation"
                    value={formData.gpssiteLocation ?? ''}
                    onChange={(e) => handleChange(e)}
                    error={Boolean(errors.gpssiteLocation)}
                    sx={{ fontSize: '12px' }}
                    InputLabelProps={{
                      shrink: true,
                    }}
                  />
                  <FormControl variant="standard" required error={Boolean(errors.siteCondition)}>
                    <InputLabel id="siteConditionlabel" required shrink={true}>
                      Site Condition
                    </InputLabel>
                    <Select
                      labelId="siteConditionlabel"
                      id="siteCondition"
                      name="siteCondition"
                      required
                      value={formData.siteCondition ?? []}
                      multiple
                      onChange={(e) => {
                        const {
                          target: { value },
                        } = e;
                        setFormData({
                          ...formData,
                          ['siteCondition']: typeof value === 'string' ? value.split(',') : value,
                          ['siteConditionOther']: isEmpty(value.find((item) => item === 'Other'))
                            ? null
                            : formData.siteConditionOther,
                        });
                        setErrors({
                          ...errors,
                          ['siteCondition']: isEmpty(value),
                        });
                      }}
                      label="Site Condition"
                      renderValue={(selected) => (
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                          {selected.map((value) => (
                            <Chip key={value} label={value} />
                          ))}
                        </Box>
                      )}
                      MenuProps={MenuProps}
                    >
                      {siteConditionsList.map((site) => (
                        <MenuItem key={site.id} value={site.name}>
                          <Checkbox checked={formData.siteCondition?.indexOf(site.name) > -1} />
                          <ListItemText primary={site.name} />
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                  {formData.siteCondition?.indexOf('Other') > -1 && (
                    <TextField
                      label="Site Condition Other"
                      id="siteConditionOther"
                      size="small"
                      variant="standard"
                      required={formData.siteCondition?.indexOf('Other') > -1}
                      name="siteConditionOther"
                      multiline
                      rows={1}
                      value={formData.siteConditionOther ?? ''}
                      onChange={(e) => handleChange(e)}
                      error={Boolean(errors.siteConditionOther)}
                      sx={{ fontSize: '12px' }}
                      InputLabelProps={{
                        shrink: true,
                      }}
                    />
                  )}
                  <TextField
                    label="Any near by ongoing projects"
                    required
                    id="nearByOngoingProjects"
                    size="small"
                    variant="standard"
                    name="nearByOngoingProjects"
                    value={formData.nearByOngoingProjects ?? ''}
                    onChange={(e) => handleChange(e)}
                    error={Boolean(errors.nearByOngoingProjects)}
                    sx={{ fontSize: '12px' }}
                    InputLabelProps={{
                      shrink: true,
                    }}
                  />
                  <FormControl variant="standard" required error={Boolean(errors.liasioningOrGovernmentWork)}>
                    <InputLabel id="liasioningOrGovernmentWorklabel" required shrink={true}>
                      Do you need us for liasioning or Government work
                    </InputLabel>
                    <Select
                      labelId="liasioningOrGovernmentWorklabel"
                      id="liasioningOrGovernmentWork"
                      name="liasioningOrGovernmentWork"
                      required
                      value={formData.liasioningOrGovernmentWork ?? []}
                      multiple
                      onChange={(e) => {
                        const {
                          target: { value },
                        } = e;
                        setFormData({
                          ...formData,
                          ['liasioningOrGovernmentWork']: typeof value === 'string' ? value.split(',') : value,
                          ['liasioningOrGovernmentWorkOther']: isEmpty(value.find((item) => item === 'Other'))
                            ? null
                            : formData.liasioningOrGovernmentWorkOther,
                        });
                        setErrors({
                          ...errors,
                          ['liasioningOrGovernmentWork']: isEmpty(value),
                        });
                      }}
                      label="Do you need us for liasioning or Government work"
                      renderValue={(selected) => (
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                          {selected.map((value) => (
                            <Chip key={value} label={value} />
                          ))}
                        </Box>
                      )}
                      MenuProps={MenuProps}
                    >
                      {liasioningOrGovernmentWorkList.map((site) => (
                        <MenuItem key={site.id} value={site.name}>
                          <Checkbox checked={formData.liasioningOrGovernmentWork?.indexOf(site.name) > -1} />
                          <ListItemText primary={site.name} />
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                  {formData.liasioningOrGovernmentWork?.indexOf('Other') > -1 && (
                    <TextField
                      label="Government work Other"
                      id="liasioningOrGovernmentWorkOther"
                      size="small"
                      variant="standard"
                      required={formData.liasioningOrGovernmentWork?.indexOf('Other') > -1}
                      name="liasioningOrGovernmentWorkOther"
                      multiline
                      rows={1}
                      value={formData.liasioningOrGovernmentWorkOther ?? ''}
                      onChange={(e) => handleChange(e)}
                      error={Boolean(errors.liasioningOrGovernmentWorkOther)}
                      sx={{ fontSize: '12px' }}
                      InputLabelProps={{
                        shrink: true,
                      }}
                    />
                  )}
                  <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={enIN}>
                    <DatePicker
                      label="Date of Preliminary sales meeting"
                      value={formData.preliminaryMeeting ? new Date(formData.preliminaryMeeting) : null}
                      onChange={(newValue) => {
                        handleChange({
                          target: {
                            name: 'preliminaryMeeting',
                            value: newValue,
                          },
                        });
                      }}
                      slotProps={{
                        textField: {
                          size: 'small',
                          variant: 'standard',
                          name: 'preliminaryMeeting',
                          fontSize: '12px',
                          InputLabelProps: { shrink: true },
                        },
                      }}
                    />
                  </LocalizationProvider>
                  <FormControl variant="standard" required error={Boolean(errors.processCompleted)}>
                    <InputLabel id="processCompletedlabel" required shrink={true}>
                      Process Completed
                    </InputLabel>
                    <Select
                      labelId="processCompletedlabel"
                      id="processCompleted"
                      name="processCompleted"
                      required
                      value={formData.processCompleted ?? []}
                      multiple
                      onChange={(e) => {
                        const {
                          target: { value },
                        } = e;
                        setFormData({
                          ...formData,
                          ['processCompleted']: typeof value === 'string' ? value.split(',') : value,
                          ['processCompletedkOther']: isEmpty(value.find((item) => item === 'Other'))
                            ? null
                            : formData.processCompletedOther,
                        });
                        setErrors({
                          ...errors,
                          ['processCompleted']: isEmpty(value),
                        });
                      }}
                      label="Process Completed"
                      renderValue={(selected) => (
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                          {selected.map((value) => (
                            <Chip key={value} label={value} />
                          ))}
                        </Box>
                      )}
                      MenuProps={MenuProps}
                    >
                      {processCompletedList.map((site) => (
                        <MenuItem key={site.id} value={site.name}>
                          <Checkbox checked={formData.processCompleted?.indexOf(site.name) > -1} />
                          <ListItemText primary={site.name} />
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                  {formData.processCompleted?.indexOf('Other') > -1 && (
                    <TextField
                      label="Process Completed Other"
                      id="processCompletedOther"
                      size="small"
                      variant="standard"
                      required={formData.processCompleted?.indexOf('Other') > -1}
                      name="processCompletedOther"
                      multiline
                      rows={1}
                      value={formData.processCompletedOther ?? ''}
                      onChange={(e) => handleChange(e)}
                      error={Boolean(errors.processCompletedOther)}
                      sx={{ fontSize: '12px' }}
                      InputLabelProps={{
                        shrink: true,
                      }}
                    />
                  )}
                  <TextField
                    label="Additional Services Promised"
                    id="additionalServicesPromised"
                    size="small"
                    variant="standard"
                    name="additionalServicesPromised"
                    value={formData.additionalServicesPromised ?? ''}
                    onChange={(e) => handleChange(e)}
                    error={Boolean(errors.additionalServicesPromised)}
                    sx={{ fontSize: '12px' }}
                    InputLabelProps={{
                      shrink: true,
                    }}
                  />
                  <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={enIN}>
                    <DatePicker
                      label="Date of Sign Up"
                      value={formData.dateOfSignUp ? new Date(formData.dateOfSignUp) : null}
                      onChange={(newValue) => {
                        handleChange({
                          target: { name: 'dateOfSignUp', value: newValue },
                        });
                      }}
                      slotProps={{
                        textField: {
                          size: 'small',
                          variant: 'standard',
                          name: 'dateOfSignUp',
                          fontSize: '12px',
                          InputLabelProps: { shrink: true },
                        },
                      }}
                    />
                  </LocalizationProvider>
                  <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={enIN}>
                    <DatePicker
                      label="Tentative Date of Agreements"
                      value={formData.tentativeAgreementDate ? new Date(formData.tentativeAgreementDate) : null}
                      onChange={(newValue) => {
                        handleChange({
                          target: {
                            name: 'tentativeAgreementDate',
                            value: newValue,
                          },
                        });
                      }}
                      slotProps={{
                        textField: {
                          size: 'small',
                          variant: 'standard',
                          name: 'tentativeAgreementDate',
                          fontSize: '12px',
                          InputLabelProps: { shrink: true },
                        },
                      }}
                    />
                  </LocalizationProvider>
                </Stack>
              </Grid>
            </Grid>
          </Stack>
          <Stack direction={'row'} sx={{ ml: 'auto' }} alignSelf={isSmallScreen ? '' : 'end'} spacing={2}>
            <Button
              variant="outlined"
              color="inherit"
              fullWidth
              size="small"
              onClick={() => handleEditLeadKycDetails(false)}
            >
              Cancel
            </Button>
            <Button variant="contained" type="submit" fullWidth color="primary" size="small">
              Update
            </Button>
          </Stack>
        </Stack>
      </Box>
    </Paper>
  );
});
